import { useTranslation } from 'react-i18next';
import { Icon, Users } from '../../ui/Icon';
import Spinner from '../../ui/Spinner';
import type { RedistributionPreview } from '../../../types';

interface Props {
  preview: RedistributionPreview;
  busy: boolean;
  onApply: () => void;
  onDismiss: () => void;
}

/** Offer, when a member joined after some expenses were logged, to fold them
 *  into the *global* (equal-among-everyone) expenses. Custom splits are never
 *  touched — that promise lives server-side (expenseRedistribution.service). */
export function ExpenseRedistributeBanner({ preview, busy, onApply, onDismiss }: Props) {
  const { t } = useTranslation();
  const names = preview.newcomers.map(n => n.name).join(', ');

  return (
    <div className="rounded-xl border border-teal-200 dark:border-teal-900/60 bg-teal-50/70 dark:bg-teal-950/30 px-4 py-3.5">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center shrink-0">
          <Icon as={Users} size={16} className="text-teal-600 dark:text-teal-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{t('expenses.redistribute.title')}</p>
          <p className="text-[12.5px] text-slate-600 dark:text-slate-300 mt-0.5 leading-snug">
            {t('expenses.redistribute.desc', { count: preview.expenseCount, names })}
          </p>
          <div className="flex items-center gap-2 mt-2.5">
            <button
              onClick={onApply}
              disabled={busy}
              className="btn-primary gap-1.5 text-xs py-1.5 px-3 disabled:opacity-60"
            >
              {busy ? <Spinner size="sm" /> : <Icon as={Users} size={14} />}
              {t('expenses.redistribute.apply')}
            </button>
            <button
              onClick={onDismiss}
              disabled={busy}
              className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 px-2 py-1.5 disabled:opacity-50 cursor-pointer"
            >
              {t('expenses.redistribute.dismiss')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
